// Stable per-route debug id for the ETA-inflation hunt (docs/OPEN_ETA_BUG.md).
//
// Derived from GEOMETRY ONLY — never duration or distance — so the same
// polyline keeps the same id even after traffic normalisation / speed parity
// rewrite its numbers. That is the point: the [nav-trace] logs print this id
// at every boundary (provider return → card → handover → NavigationView), so
// the log shows the numbers changing on a CONSTANT id (same line, rewritten
// duration) vs the id itself changing (a different route was substituted).
//
// FNV-1a over a handful of sampled vertices; collisions are irrelevant at
// "3 cards per trip" scale. Pure, cheap, no allocation beyond the string.

export function routeDebugId(r: { coordinates?: Array<[number, number]> } | null | undefined): string {
  const c = r?.coordinates;
  if (!c || c.length === 0) return "no-geom";
  let h = 2166136261 >>> 0;
  const mix = (n: number) => {
    h ^= Math.round(n * 1e5) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  };
  const picks = [0, c.length >> 2, c.length >> 1, (3 * c.length) >> 2, c.length - 1];
  for (const i of picks) {
    const p = c[i];
    if (p) { mix(p[0]); mix(p[1]); }
  }
  mix(c.length);
  return h.toString(16).padStart(8, "0");
}

/** One consistent "id/source dur dist → km/h" fragment for [nav-trace] lines. */
export function routeEtaFragment(
  r: { coordinates?: Array<[number, number]>; distance: number; duration: number },
  source?: string | null,
): string {
  const kmh = r.duration > 0 ? (r.distance / 1000) / (r.duration / 3600) : 0;
  return (
    `id=${routeDebugId(r)} src=${source ?? "?"} ` +
    `dur=${Math.round(r.duration)}s dist=${Math.round(r.distance)}m → ${kmh.toFixed(1)} km/h`
  );
}
